import * as React from 'react';

type ColorMode = 'light' | 'dark' | 'system';

interface ColorModeContextValue {
  mode: ColorMode;
  setMode: (mode: ColorMode) => void;
  /** The resolved mode (never 'system') for components that need to know. */
  resolvedMode: 'light' | 'dark';
}

const ColorModeContext = React.createContext<ColorModeContextValue>({
  mode: 'system',
  setMode: () => {},
  resolvedMode: 'light',
});

const STORAGE_KEY = 'color-mode';

function getSystemPreference(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = React.useState<ColorMode>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    } catch { /* ignore */ }
    return 'system';
  });

  const [systemPref, setSystemPref] = React.useState<'light' | 'dark'>(getSystemPreference);

  // Listen to OS preference changes
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemPref(e.matches ? 'dark' : 'light');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const resolvedMode: 'light' | 'dark' = mode === 'system' ? systemPref : mode;

  // Apply the data attribute that MUI's cssVariables system reads
  React.useEffect(() => {
    document.documentElement.setAttribute('data-mui-color-scheme', resolvedMode);
  }, [resolvedMode]);

  const setMode = React.useCallback((newMode: ColorMode) => {
    setModeState(newMode);
    try { localStorage.setItem(STORAGE_KEY, newMode); } catch { /* ignore */ }
  }, []);

  const value = React.useMemo(
    () => ({ mode, setMode, resolvedMode }),
    [mode, setMode, resolvedMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
}

export function useColorMode() {
  return React.useContext(ColorModeContext);
}
